import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import { Loader } from 'components/Loader/Loader'
import ButtonTabNavbar from 'components/UI/TabNavbar/ButtonTabNavbar'
import { usePageTitle } from 'context/pageTitleContext'
import { usePreviewContext } from 'context/previewContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { PostViewWrapper } from 'features/shared'
import { useDebounce, useFilterQuery, useInfiniteFeed } from 'helpers/hooks'
import { queryFromSearchParams } from 'helpers/util'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getLikedHistoryFeed, getMediaAutocomplete } from 'services/endpoints/media'
import { useUserProfile } from 'store'
import { useLikesHistoryFirstPageCacheStore } from 'store/likesHistoryFirstPageCacheStore'
import { type IPost } from 'types/interfaces/ITypes'

import styles from './likesHistory.module.scss'

export const LikesHistory = () => {
  const filterQuery = useFilterQuery()
  const search = filterQuery.get('searchTerm')
  const modelId = filterQuery.get('model_id')

  //  LOCAL STATE (not URL anymore)
  const [type, setType] = useState<'all' | 'photos' | 'videos'>('all')

  const { setTitle } = usePageTitle()
  const location = useLocation()
  const values = queryString.parse(location.search)
  const navigate = useNavigate()
  const userData = useUserProfile()!
  const { t } = useTranslation()

  const { setInitialData, selectedPostData, setSelectedPostData } = usePreviewContext()
  const hidePinned = true
  const [autocompleteTerm, setAutocompleteTerm] = useState<string>(search ?? '')
  const [shouldAutocomplete, setShouldAutocomplete] = useState(false)
  const [feedPosts, setFeedPosts] = useState<IPost[]>([])

  const debouncedAutocompleteTerm = useDebounce(autocompleteTerm, 500)

  const { data: autocompleteResults } = useQuery(
    ['getMediaAutocomplete', debouncedAutocompleteTerm],
    async () =>
      await getMediaAutocomplete({
        searchTerm: debouncedAutocompleteTerm
      }),
    { enabled: !!debouncedAutocompleteTerm && shouldAutocomplete }
  )

  const queryClient = useQueryClient()

  const queryKey = useMemo(
    () =>
      [
        'getLikedHistory',
        { searchTerm: search, filter: type, model_id: modelId }
      ] as [string, any],
    [search, type, modelId]
  )

  const cacheKey = useMemo(() => {
    return JSON.stringify({
      searchTerm: search ?? '',
      filter: type,
      model_id: modelId ?? ''
    })
  }, [search, type, modelId])

  const { getFirstPage, setFirstPage } = useLikesHistoryFirstPageCacheStore(state => ({
    getFirstPage: state.getFirstPage,
    setFirstPage: state.setFirstPage
  }))

  const didHydrateRef = useRef(false)

  useEffect(() => {
    if (didHydrateRef.current) return

    const cached = getFirstPage(cacheKey)
    if (!cached?.items?.length) return

    setFeedPosts(cached.items)

    queryClient.setQueryData(queryKey, {
      pages: [
        {
          nextCursor: cached.nextCursor,
          page: { data: cached.rawPageData },
          meta: cached.rawPageData?.meta
        }
      ],
      pageParams: [1]
    })

    didHydrateRef.current = true
  }, [cacheKey, getFirstPage, queryClient, queryKey])

  const {
    data,
    error,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    status,
    fetchNextPage
  } = useInfiniteFeed(queryKey, getLikedHistoryFeed)

  useEffect(() => {
    setTitle('Likes History')
  }, [setTitle])

  useEffect(() => {
    if (!search) {
      setShouldAutocomplete(true)
    }
  }, [search])

  useEffect(() => {
    if (userData) {
      setInitialData(userData.id, type, 'liked')
    }
  }, [type, userData, setInitialData])

  useEffect(() => {
    if (selectedPostData) {
      const selectedPost = feedPosts.find(post => post.id === selectedPostData.id)

      if (selectedPost) {
        setSelectedPostData(selectedPost)
      }
    }
  }, [selectedPostData, feedPosts, setSelectedPostData])

  useEffect(() => {
    if (data && !isFetching) {
      const tmp = data.pages.map(page => page.page.data.data)
      setFeedPosts([...tmp.flat()])
    }
  }, [data, isFetching])

  useEffect(() => {
    if (!data?.pages?.length) return

    const first = data.pages[0]
    const rawPageData = first?.page?.data
    const items = rawPageData?.data

    if (!rawPageData || !Array.isArray(items)) return

    setFirstPage(cacheKey, {
      rawPageData,
      items,
      nextCursor: first?.nextCursor,
      updatedAt: Date.now()
    })
  }, [cacheKey, data, setFirstPage])

  const observer = useRef<IntersectionObserver>()

  const lastPostRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage()
          }
        },
        { rootMargin: '200px' }
      )

      if (node) observer.current.observe(node)
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  )

  if (status === 'error') {
    return <p>Error: {error instanceof Error ? error.message : 'Something went wrong'}</p>
  }

  return (
/*     <WithHeaderSection
    customClass='likesHistory-whs'
      headerSection={
        <LayoutHeader
          type='search-with-buttons'
          searchValue={autocompleteTerm}
          searchFn={(term: string) => {
            if (!shouldAutocomplete) {
              setShouldAutocomplete(true)
            }
            setAutocompleteTerm(term)
          }}
          additionalProps={{ placeholder: t('searchMedia') }}
          buttons={[]}
          applyFn={() => {}}
          selectFn={(value: string, id?: number) => {
            const newValues: any = {}

            newValues.searchTerm = value
            if (id) newValues.model_id = `${id}`

            const searchQuery = queryFromSearchParams(newValues)

            setAutocompleteTerm(value)

            navigate({
              pathname: '/likes',
              search: searchQuery
            })

            setShouldAutocomplete(false)
          }}
          clearFn={() => {
            navigate('/likes')
            setAutocompleteTerm('')
          }}
          mediaSearchResults={autocompleteResults}
        />
      }
    > */
      <div className={styles.likehistorypage}>
        <div className={styles.wrapper}>
          <div className={styles.navigation}>
            <ButtonTabNavbar
              type='light'
              navArr={['all', 'photos', 'videos']}
              active={type}
              onChange={(value) => setType(value as 'all' | 'photos' | 'videos')}
              useUrl={false}
            />
          </div>

          <div className={styles.content}>
            {isFetching && !feedPosts.length ? (
              <div className={styles.loader}>
                <Loader />
              </div>
            ) : (
              <>
                {feedPosts.map((post: IPost, index: number) => (
                  <div
                    key={post.id}
                    ref={index === feedPosts.length - 1 ? lastPostRef : undefined}
                    style={{ paddingBottom: '12px', boxSizing: 'border-box' }}
                  >
                    <PostViewWrapper
                      post={post}
                      index={index}
                      userData={userData}
                      userId={userData?.id}
                      isLastPost={index === feedPosts.length - 1}
                      dataQuery={queryKey as any}
                      hidePinned={hidePinned}
                    />
                  </div>
                ))}

                {isFetchingNextPage && (
                  <div className={styles.loader}>
                    <Loader />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
/*     </WithHeaderSection> */
  )
}